<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
// Prime Insight: manual transfer to a "+ Recursos" agent — see metrica-resources.
import MetricaResourcesAPI from 'dashboard/api/metrica/resources';
import MetricaResourceHandoffAPI from 'dashboard/api/metrica/resourceHandoff';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
      // Prime Insight: "+ Recursos" manual transfer — see metrica-resources.
      resourceAgents: [],
      selectedResourceAgent: null,
      resourceAgentInstruction: '',
      isTransferringResourceAgent: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    enabledResourceAgents() {
      return this.resourceAgents.filter(agent => agent.enabled);
    },
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem.id;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    this.fetchResourceAgents();
  },
  methods: {
    // "+ Recursos" agents are optional (plan-gated) — a 403 or any other error
    // just means the section stays hidden, not a user-facing failure.
    async fetchResourceAgents() {
      try {
        const { data } = await MetricaResourcesAPI.agents();
        this.resourceAgents = data || [];
      } catch (error) {
        this.resourceAgents = [];
      }
    },
    // Shared by onClickAssignAgent/onClickAssignTeam/onSelectResourceAgent
    // below: clicking the already-selected item deselects it, otherwise it
    // becomes the new selection.
    toggleSelection(current, item) {
      return current && current.id === item.id ? null : item;
    },
    onSelectResourceAgent(selectedItem) {
      this.selectedResourceAgent = this.toggleSelection(
        this.selectedResourceAgent,
        selectedItem
      );
      // The instruction textarea is scoped to whichever agent is currently
      // selected — never let text typed for one agent silently ride along as
      // the briefing for a different one.
      this.resourceAgentInstruction = '';
    },
    async onConfirmResourceTransfer() {
      if (!this.selectedResourceAgent || this.isTransferringResourceAgent) {
        return;
      }
      this.isTransferringResourceAgent = true;
      try {
        await MetricaResourceHandoffAPI.transfer(
          this.currentChat.id,
          this.selectedResourceAgent.id,
          this.resourceAgentInstruction
        );
        useAlert(this.$t('METRICA_RESOURCES.TRANSFER.SUCCESS'));
        this.selectedResourceAgent = null;
        this.resourceAgentInstruction = '';
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            this.$t('METRICA_RESOURCES.TRANSFER.ERROR')
        );
      } finally {
        this.isTransferringResourceAgent = false;
      }
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      this.assignedAgent = this.toggleSelection(
        this.assignedAgent,
        selectedItem
      );
    },

    onClickAssignTeam(selectedItemTeam) {
      this.assignedTeam = this.toggleSelection(
        this.assignedTeam,
        selectedItemTeam
      );
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority
        ? this.priorityOptions[0]
        : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div v-if="enabledResourceAgents.length">
      <ContactDetailsItem
        compact
        :title="$t('METRICA_RESOURCES.TRANSFER.SIDEBAR_LABEL')"
      />
      <p class="px-1 mb-2 text-xs text-n-slate-11">
        {{ $t('METRICA_RESOURCES.TRANSFER.HINT') }}
      </p>
      <MultiselectDropdown
        :options="enabledResourceAgents"
        :selected-item="selectedResourceAgent"
        :multiselector-title="$t('METRICA_RESOURCES.TRANSFER.SELECT_TITLE')"
        :multiselector-placeholder="
          $t('METRICA_RESOURCES.TRANSFER.SELECT_PLACEHOLDER')
        "
        :no-search-result="$t('METRICA_RESOURCES.TRANSFER.NO_RESULTS')"
        :input-placeholder="$t('METRICA_RESOURCES.TRANSFER.SEARCH_PLACEHOLDER')"
        @select="onSelectResourceAgent"
      />
      <div v-if="selectedResourceAgent" class="flex flex-col gap-2 px-1 mt-2">
        <textarea
          v-model="resourceAgentInstruction"
          rows="2"
          class="w-full px-3 py-2 text-sm rounded-lg resize-none border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          :placeholder="
            $t('METRICA_RESOURCES.TRANSFER.INSTRUCTION_PLACEHOLDER')
          "
        />
        <NextButton
          sm
          :is-loading="isTransferringResourceAgent"
          :disabled="isTransferringResourceAgent"
          :label="$t('METRICA_RESOURCES.TRANSFER.CONFIRM')"
          @click="onConfirmResourceTransfer"
        />
      </div>
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>
